/**
 * SummaryPriorityBar — Quick Priority controls for the Summary tab.
 * Mirrors OrderSummaryPopup's Quick Priority Update (same roles, same
 * PUT /api/orders/{id} payload, same Rush/Urgent justification rule).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Zap, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const T = 'transition-[box-shadow,border-color,background-color,transform,color] duration-200 ease-out';
const PRIORITY_ROLES = ['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'];

const chip = (p) => p === 'urgent' ? 'bg-red-100 text-red-700 border-red-300' : p === 'rush' ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-amber-50 text-amber-700 border-amber-200';
const dot = (p) => p === 'urgent' ? 'bg-red-500' : p === 'rush' ? 'bg-blue-600' : 'bg-amber-400';

export const canEditSummaryPriority = (role) => PRIORITY_ROLES.includes(role);

export const SummaryPriorityBar = ({ orderData, onUpdate }) => {
  const [priority, setPriority] = useState(orderData?.priority || 'normal');
  const [notes, setNotes] = useState(orderData?.priority_notes || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setPriority(orderData?.priority || 'normal');
    setNotes(orderData?.priority_notes || '');
  }, [orderData?.priority, orderData?.priority_notes]);

  const reset = () => { setOpen(false); setPriority(orderData?.priority || 'normal'); setNotes(orderData?.priority_notes || ''); setError(''); };

  const save = async (next = priority, nextNotes = notes) => {
    if ((next === 'rush' || next === 'urgent') && !nextNotes.trim()) { setError('Justification is required for Rush/Urgent priority'); setOpen(true); return; }
    setError(''); setSaving(true);
    try {
      await axios.put(`${API}/api/orders/${orderData.id}`, { priority: next, priority_notes: nextNotes });
      setPriority(next); setOpen(false);
      toast.success(`Priority updated to ${next.toUpperCase()}`, { duration: 3000 });
      onUpdate?.({ priority: next, priority_notes: nextNotes });
    } catch (e) {
      toast.error(e.response?.data?.detail || (e.request ? 'Network error - please check your connection' : 'Failed to update priority'));
    } finally { setSaving(false); }
  };

  const current = orderData?.priority || 'normal';

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-3.5 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)]" data-testid="summary-priority-bar">
      {!open ? (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-500" aria-hidden="true" />
            <span className="text-[13px] font-semibold text-slate-700">Priority:</span>
            <span className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full border text-[11px] font-semibold uppercase tracking-wide ${chip(current)} ${T}`} data-testid="summary-priority-current">
              <span className={`h-2 w-2 rounded-full ${dot(current)}`} /> {current}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {current !== 'rush' && (
              <button type="button" onClick={() => { setPriority('rush'); setOpen(true); }} data-testid="summary-priority-rush-btn"
                className={`h-9 px-4 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-[13px] font-semibold inline-flex items-center gap-2 ${T} hover:bg-blue-100 hover:-translate-y-px`}>
                <span className="h-2.5 w-2.5 rounded-full bg-blue-600" /> Rush
              </button>
            )}
            {current !== 'urgent' && (
              <button type="button" onClick={() => { setPriority('urgent'); setOpen(true); }} data-testid="summary-priority-urgent-btn"
                className={`h-9 px-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-[13px] font-semibold inline-flex items-center gap-2 ${T} hover:bg-red-100 hover:-translate-y-px`}>
                <span className="h-2.5 w-2.5 rounded-full bg-red-500" /> Urgent
              </button>
            )}
            {current !== 'normal' && (
              <button type="button" onClick={() => save('normal', '')} disabled={saving} data-testid="summary-priority-reset-btn"
                className={`h-9 px-4 rounded-xl border border-slate-200 bg-white text-slate-600 text-[13px] font-semibold ${T} hover:bg-slate-50`}>
                Reset
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className={`rounded-xl p-3 border-2 animate-in fade-in-0 slide-in-from-top-1 duration-200 ${priority === 'urgent' ? 'border-red-200 bg-red-50/40' : priority === 'rush' ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-white'}`} data-testid="summary-priority-panel">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[13px] font-bold text-slate-800 flex items-center gap-2">
              Quick Priority Update
              <span className={`px-2 py-0.5 rounded-full border text-[10.5px] font-semibold uppercase ${chip(priority)}`}>{priority}</span>
            </h4>
            <button type="button" onClick={reset} className="h-7 w-7 grid place-items-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" aria-label="Close" data-testid="summary-priority-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
            <div>
              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1">Priority Level <span className="text-red-500">*</span></label>
              <select value={priority} onChange={(e) => { setPriority(e.target.value); setError(''); }} data-testid="summary-priority-select"
                className={`w-full h-9 px-3 border rounded-lg text-[13px] font-medium bg-white ${priority === 'urgent' ? 'border-red-300 text-red-700' : priority === 'rush' ? 'border-blue-300 text-blue-700' : 'border-slate-300 text-slate-700'}`}>
                <option value="normal">Normal</option>
                <option value="rush">Rush</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-500 mb-1">Justification {(priority === 'rush' || priority === 'urgent') && <span className="text-red-500">*</span>}</label>
              <input type="text" value={notes} onChange={(e) => { setNotes(e.target.value); setError(''); }} data-testid="summary-priority-notes"
                placeholder={priority === 'normal' ? 'Optional...' : 'Required: Why is this urgent?'}
                className={`w-full h-9 px-3 border rounded-lg text-[13px] ${error ? 'border-red-500 bg-red-50' : 'border-slate-300'}`} />
            </div>
          </div>
          {error && <p className="text-red-600 text-xs mb-2" data-testid="summary-priority-error">{error}</p>}
          <div className="flex items-center justify-end gap-2">
            <button type="button" onClick={reset} className="h-9 px-4 text-slate-600 hover:bg-slate-100 rounded-lg text-[13px] font-medium" data-testid="summary-priority-cancel">Cancel</button>
            <button type="button" onClick={() => save()} disabled={saving} data-testid="summary-priority-save"
              className={`h-9 px-5 rounded-lg text-[13px] font-semibold text-white shadow-sm ${T} hover:shadow-md ${priority === 'urgent' ? 'bg-red-600 hover:bg-red-700' : priority === 'rush' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-600 hover:bg-slate-700'} ${saving ? 'opacity-75 cursor-not-allowed' : ''}`}>
              {saving ? 'Saving…' : 'Save Priority'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SummaryPriorityBar;
